import { ReactNode } from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  LinearProgress,
  alpha,
  linearProgressClasses,
  ButtonProps,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { useSetStatusBarStyle } from 'hooks/useSetStatusBarStyle';

type OnboardingProgress = {
  current: number;
  total: number;
};

type OnboardingLayoutProps = {
  title?: ReactNode;
  children: ReactNode;
  footerText?: string | ReactNode;
  buttonTitle?: string;
  buttonVariant?: ButtonProps['variant'];
  buttonColor?: ButtonProps['color'];
  onClick?: () => void;
  progress?: OnboardingProgress;
  hideProgress?: boolean;
  containerGap?: number | string;
};

export type { OnboardingProgress, OnboardingLayoutProps };

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';
  const fillColor = isDark
    ? theme.palette.brand[500]
    : theme.palette.primary.main;

  return {
    root: {
      position: 'relative',
      width: '100%',
      height: '100%',
      backgroundColor: theme.palette.background.headerAppBar,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    container: {
      color: theme.palette.text.primary,
      paddingLeft: `max(env(safe-area-inset-left), 16px)`,
      paddingRight: `max(env(safe-area-inset-right), 16px)`,
      paddingTop: `max(env(safe-area-inset-top), 16px)`,
      paddingBottom: `max(env(safe-area-inset-bottom), 16px)`,
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100%',
    },
    progress: {
      height: 6,
      borderRadius: 999,
      backgroundColor: alpha(fillColor, 0.3),
      [`& .${linearProgressClasses.bar}`]: {
        backgroundColor: fillColor,
        borderRadius: 999,
        transition: 'transform 600ms ease-out',
      },
    },
  };
});

export const OnboardingLayout = (props: OnboardingLayoutProps): JSX.Element => {
  const {
    title,
    children,
    footerText,
    buttonTitle,
    buttonVariant,
    buttonColor,
    onClick,
    progress,
    hideProgress,
    containerGap = 4,
  } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  useSetStatusBarStyle(theme.palette.background.headerAppBar);

  // Trailing buffer so the final step doesn't fill the bar.
  const progressValue = progress
    ? Math.max(
        0,
        Math.min(100, (progress.current / (progress.total + 1)) * 100),
      )
    : 0;

  return (
    <Box className={classes.root}>
      <Container className={classes.container} sx={{ gap: containerGap }}>
        <Stack spacing={3} sx={{ flex: '1 0 auto' }}>
          <Typography
            variant="h2"
            align="center"
            data-testid="onboarding-layout-title"
          >
            {title}
          </Typography>

          <Box
            sx={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </Box>
        </Stack>

        <Stack spacing={2} sx={{ flexShrink: 0 }}>
          {Boolean(footerText) && footerText}

          {Boolean(buttonTitle) && (
            <Button
              fullWidth
              size="large"
              onClick={onClick}
              data-testid="onboarding-button"
              variant={buttonVariant}
              color={buttonColor}
            >
              {buttonTitle}
            </Button>
          )}

          {progress && !hideProgress && (
            <Box px={6}>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                className={classes.progress}
                aria-label={t('onboarding.progressLabel')}
                aria-valuenow={progress.current}
                aria-valuemin={0}
                aria-valuemax={progress.total}
                data-testid="onboarding-progress"
              />
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};
